import { Link } from 'react-router-dom';

import { useShellChrome } from './surface-chrome';
import styles from './AppShell.module.css';

/**
 * The 40px band under the header: where you are, whether what you are looking at
 * is valid, and what you can do to it.
 *
 * All three come from the surface via `useSurfaceChrome`. The band renders even
 * when a surface publishes nothing, because a chrome row that appears and
 * disappears shifts the whole viewport by 40px on every navigation.
 *
 * The breadcrumb is hand-rolled rather than the design system's `Breadcrumb`: that
 * component navigates by real `href`, which is correct for a document and wrong
 * inside a single-page router — every crumb click would reload the app and lose
 * the query cache. The markup and the semantics are the same; only the navigation
 * mechanism differs.
 */

export function ShellSubhead() {
  const { breadcrumb, status, actions } = useShellChrome();

  return (
    <div className={styles.subhead}>
      {breadcrumb.length > 0 && (
        <nav className={styles.crumb} aria-label="Breadcrumb">
          <ol className={styles.crumbList}>
            {breadcrumb.map((item, index) => {
              const last = index === breadcrumb.length - 1;
              return (
                // Labels are unique within a trail and there is no stable id to
                // key on; the index is the position, which is what identifies a
                // crumb.
                <li key={index} className={styles.crumbItem}>
                  {item.to !== undefined && !last ? (
                    <Link to={item.to} className={styles.crumbLink}>
                      {item.label}
                    </Link>
                  ) : (
                    <span className={styles.crumbCurrent} aria-current={last ? 'page' : undefined}>
                      {item.label}
                    </span>
                  )}
                  {!last && (
                    <span className={styles.crumbSeparator} aria-hidden="true">
                      /
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      )}

      {status !== null && (
        // The dot is decoration: `label` already says "0 errors" in words, so a
        // screen reader hears the fact and a forced-colours user still reads it.
        <p className={styles.subheadStatus}>
          <span className={styles.statusDot} data-tone={status.tone} aria-hidden="true" />
          {status.label}
        </p>
      )}

      <span className={styles.spacer} />

      {actions !== null && <div className={styles.subheadActions}>{actions}</div>}
    </div>
  );
}
